import { useContext, useEffect, useMemo, useRef, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { normalizeToDoARJourneyContext, sameJourneyContext } from "./toDoARJourneyContext";
import { SITE_ORIGIN as MARKETPLACE_ORIGIN, AI_ORIGIN, SPACE_ORIGIN } from "../../utils/productOrigins";
import {
  bindGuestJourneyContext,
  continuePublicReality,
  createGuestClaimCode,
  getDoarSpatialSession,
  getGatewaySpatialSession,
  getGuestAssetUrl,
  getGuestJob,
  getGuestSessionStatus,
  prepareGuestAr,
  refineGuestAr,
  startGuestSession,
  submitGuestReference,
  transcribeGuestAudio,
  uploadGuestFile,
} from "../../api/doarPlatformApi";

const TOKEN_KEY = "doar_gateway_guest_token";
const ACTIVE_KEY = "doar_gateway_active_v2";
const ACCEPTED_FILES = "image/*,video/*,.glb,.gltf,.usdz,.obj,.fbx,.stl,.ply,.dae,.blend,.pdf,.txt,.doc,.docx,.rtf,.ppt,.pptx,.rvt,.ifc,.dwg,.dxf,.3dm,.las,.laz,.e57,.pts,.xyz";
const MAX_VOICE_MS = 30000;

const TODOAR_PHASE = Object.freeze({
  IDLE: "idle",
  LISTENING: "listening",
  UNDERSTANDING_SOURCE: "understanding-source",
  PREPARING_AR: "preparing-ar",
  CLAIMING: "claiming",
  UPDATING: "updating",
  AR_READY: "ar-ready",
  SOURCE_ONLY: "source-only",
  NEEDS_EVIDENCE: "needs-evidence",
  FAILED_RECOVERABLE: "failed-recoverable",
  OWNED: "owned",
  EXPIRED: "expired",
});

function restoredGatewayState() {
  try {
    const raw = sessionStorage.getItem(ACTIVE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

function isSpatialClassification(classification) {
  return classification?.prepareAr === true || classification?.target === "spatial";
}

function durablePreview(preview) {
  if (!preview || typeof preview !== "object") return null;
  return {
    assetId: preview.assetId || null,
    iosAssetId: preview.iosAssetId || null,
    format: preview.format || null,
    webAr: preview.webAr === true,
    iosAr: preview.iosAr === true,
  };
}

function hydrateGuestPreview(preview, guestToken) {
  const stable = durablePreview(preview);
  if (!stable || !guestToken) return stable;
  const glbAssetId = stable.webAr ? stable.assetId : null;
  const iosAssetId = stable.iosAssetId || (stable.iosAr && !stable.webAr ? stable.assetId : null);
  const glbUrl = glbAssetId ? getGuestAssetUrl(glbAssetId, guestToken) : null;
  const usdzUrl = iosAssetId ? getGuestAssetUrl(iosAssetId, guestToken) : null;
  return {
    ...stable,
    previewUrl: glbUrl || usdzUrl,
    glbUrl,
    usdzUrl,
  };
}

function durableSourceInfo(sourceInfo) {
  if (!sourceInfo || typeof sourceInfo !== "object") return sourceInfo || null;
  const stable = { ...sourceInfo };
  delete stable.previewUrl;
  delete stable.webArUrl;
  delete stable.iosArUrl;
  return stable;
}

function recorderMimeType() {
  if (typeof MediaRecorder === "undefined") return "";
  for (const type of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
    if (!MediaRecorder.isTypeSupported || MediaRecorder.isTypeSupported(type)) return type;
  }
  return "";
}

function phaseFor({ busy, recording, status, error, preview, doarId, ownerState, classification }) {
  if (ownerState === "expired") return TODOAR_PHASE.EXPIRED;
  if (ownerState === "owned") return TODOAR_PHASE.OWNED;
  if (recording) return TODOAR_PHASE.LISTENING;
  if (busy) {
    if (String(status || "").startsWith("Reading")) return TODOAR_PHASE.UNDERSTANDING_SOURCE;
    if (String(status || "").startsWith("Continuing")) return TODOAR_PHASE.CLAIMING;
    if (String(status || "").startsWith("Updating")) return TODOAR_PHASE.UPDATING;
    return TODOAR_PHASE.PREPARING_AR;
  }
  if (preview) return TODOAR_PHASE.AR_READY;
  if (error && doarId) return TODOAR_PHASE.FAILED_RECOVERABLE;
  if (doarId && isSpatialClassification(classification)) return TODOAR_PHASE.NEEDS_EVIDENCE;
  if (doarId) return TODOAR_PHASE.SOURCE_ONLY;
  if (error) return TODOAR_PHASE.FAILED_RECOVERABLE;
  return TODOAR_PHASE.IDLE;
}

export default function useToDoARSession({ journeyContext: ambientJourneyContext = null } = {}) {
  const { token: accountToken } = useContext(AuthContext);
  const restored = useRef(null);
  if (restored.current == null) restored.current = restoredGatewayState();
  const initial = restored.current;
  const normalizedAmbientJourneyContext = useMemo(
    () => normalizeToDoARJourneyContext(ambientJourneyContext),
    [ambientJourneyContext]
  );
  const [journeyContext, setJourneyContext] = useState(() => (
    normalizeToDoARJourneyContext(initial.journeyContext) || normalizedAmbientJourneyContext
  ));

  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_KEY));
  const [textValue, setTextValue] = useState(() => initial.textValue || "");
  const [doarId, setDoarId] = useState(() => initial.doarId || null);
  const [classification, setClassification] = useState(() => initial.classification || null);
  const [preview, setPreview] = useState(() => hydrateGuestPreview(initial.preview, sessionStorage.getItem(TOKEN_KEY)));
  const [spatialSession, setSpatialSession] = useState(null);
  const [spatialSessionLoading, setSpatialSessionLoading] = useState(false);
  const [jobId, setJobId] = useState(() => initial.jobId || null);
  const [revisionId, setRevisionId] = useState(() => initial.revisionId || null);
  const [status, setStatus] = useState(() => initial.status || "");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(() => Boolean(initial.jobId));
  const [recording, setRecording] = useState(false);
  const [refineOpen, setRefineOpen] = useState(false);
  const [refineText, setRefineText] = useState("");
  const [sourceLabel, setSourceLabel] = useState(() => initial.sourceLabel || "");
  const [sourceInfo, setSourceInfo] = useState(() => initial.sourceInfo || null);
  const [ownerState, setOwnerState] = useState(() => initial.ownerState || "guest");
  const [continuationMode, setContinuationMode] = useState(() => initial.continuationMode || null);
  const recorderRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const handoffPendingRef = useRef(false);

  useEffect(() => {
    const hasDraft = Boolean(String(textValue || "").trim());
    const hasJourneyContext = Boolean(journeyContext);
    if (!doarId && !jobId && !sourceInfo && !classification && !hasDraft && !hasJourneyContext) {
      sessionStorage.removeItem(ACTIVE_KEY);
      return;
    }
    sessionStorage.setItem(ACTIVE_KEY, JSON.stringify({
      journeyContext,
      doarId,
      jobId,
      classification,
      sourceInfo: durableSourceInfo(sourceInfo),
      sourceLabel,
      textValue,
      preview: durablePreview(preview),
      revisionId,
      status,
      ownerState,
      continuationMode,
    }));
  }, [journeyContext, doarId, jobId, classification, sourceInfo, sourceLabel, textValue, preview, revisionId, status, ownerState, continuationMode]);

  useEffect(() => {
    if (!normalizedAmbientJourneyContext) return;
    if (journeyContext) return;
    if (doarId || jobId || sourceInfo || classification || String(textValue || "").trim()) return;
    setJourneyContext(normalizedAmbientJourneyContext);
  }, [
    normalizedAmbientJourneyContext,
    journeyContext,
    doarId,
    jobId,
    sourceInfo,
    classification,
    textValue,
  ]);

  useEffect(() => {
    if (token || doarId) return undefined;
    let live = true;
    startGuestSession(journeyContext || normalizedAmbientJourneyContext)
      .then(result => {
        if (!live) return;
        sessionStorage.setItem(TOKEN_KEY, result.guestToken);
        setToken(result.guestToken);
        const bound = normalizeToDoARJourneyContext(result?.journeyContext);
        if (bound) setJourneyContext(bound);
      })
      .catch(err => setError(
        err?.response?.data?.message
        || "Couldn't start a private DoAR. Refresh and try again."
      ));
    return () => { live = false; };
  }, [token, doarId, journeyContext, normalizedAmbientJourneyContext]);

  useEffect(() => () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    try { recorderRef.current?.stop(); } catch {}
    streamRef.current?.getTracks?.().forEach(track => track.stop());
  }, []);

  useEffect(() => {
    const contextToBind = journeyContext || normalizedAmbientJourneyContext;
    if (!token || !contextToBind || doarId || jobId) return undefined;
    let live = true;
    bindGuestJourneyContext(token, contextToBind)
      .then(result => {
        if (!live) return;
        const bound = normalizeToDoARJourneyContext(result?.journeyContext);
        if (bound) setJourneyContext(bound);
      })
      .catch(err => {
        if (!live) return;
        const code = err?.response?.data?.code;
        if (code === "JOURNEY_CONTEXT_LOCKED" || code === "REALITY_CONTEXT_STALE" || code === "INVALID_JOURNEY_CONTEXT") {
          setError(err.response.data.message || "This Reality context could not be continued.");
        }
      });
    return () => { live = false; };
  }, [token, journeyContext, normalizedAmbientJourneyContext, doarId, jobId]);

  useEffect(() => {
    if (!doarId || ownerState !== "owned" || !accountToken) return undefined;
    let live = true;
    setSpatialSessionLoading(true);
    getDoarSpatialSession(doarId)
      .then(owned => {
        if (!live) return;
        const next = owned?.session?.preview || {};
        setPreview(next.glbUrl || next.usdzUrl ? {
          assetId: next.sourceAssetId || null,
          previewUrl: next.glbUrl || next.usdzUrl,
          glbUrl: next.glbUrl || null,
          usdzUrl: next.usdzUrl || null,
          webAr: Boolean(next.glbUrl),
          iosAr: Boolean(next.usdzUrl),
        } : null);
        setSpatialSession(owned?.session || null);
        setSpatialSessionLoading(false);
        setStatus("Saved to your account.");
        setError("");
      })
      .catch(() => {
        if (!live) return;
        setPreview(null);
        setSpatialSession(null);
        setSpatialSessionLoading(false);
      });
    return () => { live = false; };
  }, [doarId, ownerState, accountToken]);

  useEffect(() => {
    const spatialCandidate = Boolean(preview || isSpatialClassification(classification));
    if (!token || !doarId || ownerState !== "guest" || !spatialCandidate || jobId) return undefined;
    let live = true;
    setSpatialSessionLoading(true);
    getGatewaySpatialSession(token, doarId)
      .then(result => {
        if (!live) return;
        setSpatialSession(result?.session || null);
        setSpatialSessionLoading(false);
      })
      .catch(() => {
        if (!live) return;
        setSpatialSession(null);
        setSpatialSessionLoading(false);
      });
    return () => { live = false; };
  }, [
    token,
    doarId,
    ownerState,
    jobId,
    classification,
    preview,
    revisionId,
  ]);

  useEffect(() => {
    const hasLocalWork = Boolean(doarId || journeyContext || String(textValue || "").trim());
    if (!token || !hasLocalWork) return undefined;
    let live = true;

    const reconcile = async () => {
      try {
        const state = await getGuestSessionStatus(token);
        if (!live) return;
        const serverJourneyContext = normalizeToDoARJourneyContext(state?.journeyContext);
        if (serverJourneyContext) setJourneyContext(serverJourneyContext);
        if (state?.active) {
          if (handoffPendingRef.current || String(status || "").startsWith("Continuing")) {
            handoffPendingRef.current = false;
            setBusy(false);
            setStatus("");
          }
          return;
        }
        handoffPendingRef.current = false;
        setJobId(null);
        setBusy(false);
        setError("");

        if (state?.claimed) {
          setOwnerState("owned");
          setStatus(accountToken ? "Saved to your account." : "This DoAR is saved. Sign in on TheDoAR to continue.");
          if (accountToken) {
            try {
              const owned = await getDoarSpatialSession(doarId);
              const next = owned?.session?.preview || {};
              if (!live) return;
              setPreview(next.glbUrl || next.usdzUrl ? {
                assetId: next.sourceAssetId || null,
                previewUrl: next.glbUrl || next.usdzUrl,
                glbUrl: next.glbUrl || null,
                usdzUrl: next.usdzUrl || null,
                webAr: Boolean(next.glbUrl),
                iosAr: Boolean(next.usdzUrl),
              } : null);
            } catch {
              if (live) setPreview(null);
            }
          } else {
            setPreview(null);
          }
          return;
        }

        if (state?.expired || state?.status === "missing") {
          if (!doarId) {
            sessionStorage.removeItem(TOKEN_KEY);
            setToken(null);
            setStatus("Restoring your private session…");
            setError("");
            return;
          }
          setOwnerState("expired");
          setPreview(null);
          setStatus("");
          setError("This temporary creation expired. Start a new DoAR to continue.");
        }
      } catch {}
    };

    const onResume = () => reconcile();
    const onPageHide = () => {
      // If navigation succeeds the page is leaving; if the browser later
      // restores this page from bfcache, pageshow reconciles canonical state
      // and releases any stale "Continuing…" UI.
      if (handoffPendingRef.current) setStatus("Continuing with the same DoAR…");
    };
    reconcile();
    window.addEventListener("pageshow", onResume);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("focus", onResume);
    document.addEventListener("visibilitychange", onResume);
    return () => {
      live = false;
      window.removeEventListener("pageshow", onResume);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("focus", onResume);
      document.removeEventListener("visibilitychange", onResume);
    };
  }, [token, doarId, journeyContext, textValue, accountToken, status]);

  useEffect(() => {
    if (!jobId || !token) return undefined;
    let live = true;
    let timer;

    const poll = async () => {
      try {
        const response = await getGuestJob(token, jobId);
        if (!live) return;

        if (response.job?.operation === "INGEST_SOURCE" && response.job?.status === "succeeded") {
          const understood = response.job.source || null;
          const next = response.job.next || null;
          setSourceInfo(understood);
          setClassification(understood?.classification || null);
          setSourceLabel(understood?.title || "Source understood");
          if (response.job.revisionId) setRevisionId(response.job.revisionId);
          setError("");

          if (next?.state === "ready" && next?.previewUrl) {
            const iosOnly = next?.format === "usdz" || next?.iosOnly === true;
            setPreview({
              assetId: next.assetId || understood?.webArAssetId || understood?.iosArAssetId || null,
              previewUrl: next.previewUrl,
              glbUrl: iosOnly ? null : next.previewUrl,
              usdzUrl: iosOnly ? next.previewUrl : null,
              webAr: !iosOnly,
              iosAr: iosOnly,
            });
            setJobId(null);
            setBusy(false);
            setStatus("");
            return;
          }

          if (next?.jobId) {
            setJobId(next.jobId);
            setBusy(true);
            setStatus(understood?.title
              ? "Found " + understood.title + " · preparing it in 3D…"
              : "Preparing the product in 3D…");
            return;
          }

          if (next?.state === "needs_attention") {
            setJobId(null);
            setBusy(false);
            setStatus("");
            setError(next.message || "The product is understood, but a reliable 3D representation could not be prepared yet.");
            return;
          }

          setJobId(null);
          setBusy(false);
          setStatus("");
          return;
        }

        if (response.job?.status === "succeeded" && response.job?.asset?.previewUrl) {
          const glb = response.job.asset?.format === "glb" ? response.job.asset : null;
          const usdz = response.job.iosAsset?.format === "usdz"
            ? response.job.iosAsset
            : response.job.asset?.format === "usdz"
              ? response.job.asset
              : null;
          setPreview({
            assetId: glb?.id || response.job.asset?.id || null,
            iosAssetId: usdz?.id || null,
            previewUrl: glb?.previewUrl || usdz?.previewUrl || response.job.asset.previewUrl,
            glbUrl: glb?.previewUrl || null,
            usdzUrl: usdz?.previewUrl || null,
            webAr: Boolean(glb),
            iosAr: Boolean(usdz),
          });
          if (response.job.revisionId) setRevisionId(response.job.revisionId);
          setJobId(null);
          setBusy(false);
          setError("");
          setStatus("");
          return;
        }

        if (response.job?.status === "failed") {
          setJobId(null);
          setBusy(false);
          setStatus("");
          setError(response.job?.errorMessage || (response.job?.operation === "INGEST_SOURCE"
            ? "DoAR could not understand that source yet. Add a clearer image, screenshot, or description."
            : "The spatial preview could not be prepared. Retry or add another reference."));
          return;
        }
      } catch {}

      timer = setTimeout(poll, 2500);
    };

    poll();
    return () => {
      live = false;
      clearTimeout(timer);
    };
  }, [jobId, token]);

  const acceptPreparedAr = prepared => {
    if (prepared?.state === "ready" && prepared?.preview?.previewUrl) {
      setPreview({
        ...prepared.preview,
        glbUrl: prepared.preview.webAr ? prepared.preview.previewUrl : null,
        usdzUrl: prepared.preview.iosAr ? prepared.preview.previewUrl : null,
      });
      setJobId(null);
      setBusy(false);
      setError("");
      setStatus("");
      return;
    }

    if (prepared?.job?.id) {
      setJobId(prepared.job.id);
      setStatus("Building the spatial representation…");
      return;
    }

    setJobId(null);
    setBusy(false);
    setStatus(prepared?.message || "No AR-ready representation was produced yet.");
  };

  const acceptResult = async (result, sourcePrompt = "") => {
    const id = String(result?.objectId || result?.doarId || "");
    setDoarId(id || null);
    setRevisionId(result?.revisionId || null);
    setClassification(result?.classification || null);
    setPreview(result?.preview?.previewUrl ? result.preview : null);

    if (result?.job?.id) {
      setJobId(result.job.id);
      setStatus(result.job.operation === "INGEST_SOURCE"
        ? "Reading the source…"
        : "Creating the spatial representation…");
      return;
    }

    if (!id) {
      setBusy(false);
      setError("DoAR could not establish an identity for this input.");
      return;
    }

    if (result?.preview?.ar && result.preview.previewUrl) {
      setBusy(false);
      setError("");
      setStatus("");
      return;
    }

    if (!isSpatialClassification(result?.classification)) {
      setBusy(false);
      setError("");
      setStatus("");
      return;
    }

    setStatus("Preparing AR from your source…");
    try {
      acceptPreparedAr(await prepareGuestAr(token, id, sourcePrompt));
    } catch (err) {
      setBusy(false);
      setStatus("");
      setError(err?.response?.data?.message || "The spatial preview could not be prepared. Try again, ask DoAR, or continue in Space.");
    }
  };

  const resolveRealityContinuation = async (intent = "continue") => {
    if (!journeyContext?.slug || busy || recording) return { mode: "PUBLIC_CONTEXT_ONLY" };
    setBusy(true);
    setError("");
    setStatus("Checking how this Reality can continue…");
    try {
      const result = await continuePublicReality(journeyContext.slug, intent, token);
      const mode = String(result?.mode || "");

      if (mode === "SAME_DOAR") {
        setContinuationMode("SAME_DOAR");
        setBusy(false);
        setStatus(result?.message || "You own this Reality.");
        if (result?.destination?.space) window.location.assign(result.destination.space);
        return result;
      }

      if (mode === "DERIVED_DOAR") {
        setContinuationMode("DERIVED_DOAR");
        setDoarId(result.objectId || null);
        setRevisionId(result.revisionId || null);
        setClassification({ referenceType: "reality", target: "source", kind: "unknown", prepareAr: false });
        setPreview(null);
        setSourceLabel(result?.source?.title || journeyContext.title || "Source Reality");
        setSourceInfo({
          title: result?.source?.title || journeyContext.title || "Source Reality",
          sourceKind: "reality",
          sourceRealitySlug: result?.source?.slug || journeyContext.slug,
          sourceDoarId: result?.source?.objectId || journeyContext.doarId,
          sourceRevisionId: result?.source?.revisionId || journeyContext.revisionId,
          rightsMode: "derived",
        });
        setOwnerState(result?.ownerType === "user" ? "owned" : "guest");
        setBusy(false);
        setStatus(result?.message || "A new DoAR was created from this Reality.");
        return result;
      }

      if (mode === "PUBLIC_CONTEXT_ONLY") {
        setContinuationMode("PUBLIC_CONTEXT_ONLY");
        setBusy(false);
        setStatus(result?.message || "This Reality is available as read-only context.");
        return result;
      }

      setContinuationMode("ACTION_NOT_PERMITTED");
      setBusy(false);
      setStatus("");
      setError(result?.message || "This Reality cannot be modified from the public experience.");
      return result;
    } catch (err) {
      setBusy(false);
      setStatus("");
      setError(err?.response?.data?.message || err?.message || "This Reality could not be continued.");
      return null;
    }
  };

  const createFromText = async raw => {
    const clean = String(raw == null ? textValue : raw).trim();
    if (!clean || !token || busy || recording || doarId) return false;
    setTextValue(clean);

    if (journeyContext?.slug) {
      const continuation = await resolveRealityContinuation("continue");
      if (!continuation) return false;
      if (continuation.mode === "SAME_DOAR") return true;
      if (continuation.mode === "DERIVED_DOAR") {
        setTextValue(clean);
        setBusy(true);
        setError("");
        setStatus("Applying your request to the new DoAR…");
        try {
          await acceptResult(await submitGuestReference(token, clean), clean);
          return true;
        } catch (err) {
          setBusy(false);
          setStatus("");
          setError(err?.response?.data?.message || err.message || "Couldn't continue that DoAR yet.");
          return false;
        }
      }
    }

    setBusy(true);
    setError("");
    setStatus(/^https?:\/\//i.test(clean)
      ? "Reading your source…"
      : "Understanding your input and establishing a DoAR…");
    try {
      await acceptResult(await submitGuestReference(token, clean), clean);
      return true;
    } catch (err) {
      setBusy(false);
      setError(err?.response?.data?.message || err.message || "Couldn't create that DoAR yet.");
      return false;
    }
  };

  const addFile = async (file, { label = null } = {}) => {
    if (!file || !token || busy || recording) return false;

    if (journeyContext?.slug && !doarId) {
      const continuation = await resolveRealityContinuation("continue");
      if (!continuation) return false;
      if (continuation.mode === "SAME_DOAR") return true;
    }

    setBusy(true);
    setError("");
    setSourceLabel(label || file.name || "Your reference");
    setStatus("Reading the source and creating a DoAR…");
    try {
      await acceptResult(await uploadGuestFile(token, file), file.name || "Uploaded source");
      return true;
    } catch (err) {
      setBusy(false);
      setError(err?.response?.data?.message || err.message || "Couldn't use that source yet.");
      return false;
    }
  };

  const stopVoice = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = null;
    if (recorderRef.current?.state === "recording") recorderRef.current.stop();
  };

  const toggleVoice = async () => {
    if (recording) {
      stopVoice();
      return;
    }
    if (busy || doarId) return;

    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("Voice isn't available in this browser. Type, sketch or add a source instead.");
      return;
    }

    setError("");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];
      const mimeType = recorderMimeType();
      const recorder = mimeType ? new MediaRecorder(stream, { mimeType }) : new MediaRecorder(stream);
      recorderRef.current = recorder;
      recorder.ondataavailable = event => {
        if (event.data?.size) chunksRef.current.push(event.data);
      };
      recorder.onstop = async () => {
        setRecording(false);
        stream.getTracks().forEach(track => track.stop());
        streamRef.current = null;
        const audio = new Blob(chunksRef.current, { type: recorder.mimeType || "audio/webm" });
        chunksRef.current = [];
        if (!audio.size) {
          setError("I didn't catch that. Try again or type instead.");
          return;
        }

        setBusy(true);
        setStatus("Understanding what you said…");
        try {
          const transcript = String((await transcribeGuestAudio(audio, navigator.language || ""))?.text || "").trim();
          if (!transcript) throw new Error("No words heard");
          setTextValue(transcript);
          setSourceLabel(transcript);
          if (journeyContext?.slug && !doarId) {
            const continuation = await resolveRealityContinuation("continue");
            if (!continuation || continuation.mode === "SAME_DOAR") return;
          }
          setBusy(true);
          setStatus("Applying what you said…");
          await acceptResult(await submitGuestReference(token, transcript), transcript);
        } catch (err) {
          setBusy(false);
          setError(err?.response?.data?.message || "I couldn't understand that recording.");
        }
      };
      recorder.start(250);
      setRecording(true);
      setStatus("Listening… tap the microphone again when you're done.");
      timerRef.current = setTimeout(stopVoice, MAX_VOICE_MS);
    } catch {
      setError("Microphone permission is off. Allow microphone access, or type, sketch or add a source instead.");
    }
  };

  const continueTo = async kind => {
    if (!doarId || busy || ownerState === "expired") return false;
    const target = kind === "ask" ? "ai" : kind === "edit" ? "space" : "marketplace";
    handoffPendingRef.current = true;
    setBusy(true);
    setError("");
    setStatus("Continuing with the same DoAR…");

    try {
      let claimCode = null;
      if (ownerState === "guest") {
        if (!token) throw new Error("This temporary DoAR can no longer be continued.");
        const claim = await createGuestClaimCode(token, target);
        claimCode = String(claim?.code || "");
        if (!claimCode) throw new Error("DoAR handoff could not be created.");
      }

      const base = kind === "ask"
        ? AI_ORIGIN
        : kind === "edit"
          ? SPACE_ORIGIN + "/d/" + encodeURIComponent(doarId)
          : MARKETPLACE_ORIGIN + "/publish";
      const url = new URL(base);
      url.searchParams.set("doar", doarId);
      url.searchParams.set("object", doarId);
      if (revisionId) url.searchParams.set("revision", revisionId);
      if (preview?.assetId) url.searchParams.set("asset", preview.assetId);
      if (claimCode) url.searchParams.set("claim", claimCode);
      url.searchParams.set("from", "todoar");
      url.searchParams.set("intent", kind);

      if (kind === "publish") {
        if (sourceInfo?.title) url.searchParams.set("title", sourceInfo.title);
        const externalProduct = sourceInfo?.isProduct === true
          || sourceInfo?.sourceKind === "product"
          || classification?.kind === "product";
        const rawCategory = String(sourceInfo?.semanticCategory || "").trim();
        const publishCategory = !externalProduct && (rawCategory === "physical_product" || rawCategory === "product")
          ? (classification?.kind === "spatial" ? "spatial" : "object")
          : rawCategory;
        if (publishCategory) url.searchParams.set("category", publishCategory);

        if (externalProduct && sourceInfo?.sourceUrl) {
          url.searchParams.set("productUrl", sourceInfo.sourceUrl);
          url.searchParams.set("usage", "external_product");
        } else {
          url.searchParams.delete("productUrl");
          url.searchParams.set("usage", "view_only");
        }
      }

      window.location.assign(url.toString());
      return true;
    } catch (err) {
      handoffPendingRef.current = false;
      setBusy(false);
      setStatus("");
      setError(err?.response?.data?.message || err?.message || "Couldn't continue this DoAR.");
      return false;
    }
  };

  const retrySpatial = async () => {
    if (!token || !doarId || busy) return false;
    setBusy(true);
    setError("");
    setStatus("Preparing AR from your source…");
    try {
      acceptPreparedAr(await prepareGuestAr(token, doarId, sourceLabel || textValue || "Spatial DoAR"));
      return true;
    } catch (err) {
      setBusy(false);
      setStatus("");
      setError(err?.response?.data?.message || "The spatial preview could not be prepared. Try again, ask DoAR, or continue in Space.");
      return false;
    }
  };

  const refine = async raw => {
    const clean = String(raw == null ? refineText : raw).trim();
    if (!clean || !preview || !doarId || busy) return false;
    setRefineText(clean);
    setBusy(true);
    setError("");
    setStatus("Updating this spatial augmentation…");
    try {
      const result = await refineGuestAr(token, doarId, clean);
      if (result.job?.id) {
        setJobId(result.job.id);
        return true;
      }
      setBusy(false);
      setError("That refinement could not start.");
      return false;
    } catch (err) {
      setBusy(false);
      setError(err?.response?.data?.message || "Couldn't refine it yet.");
      return false;
    }
  };

  const reset = ({ preserveAmbientContext = true } = {}) => {
    stopVoice();
    handoffPendingRef.current = false;
    sessionStorage.removeItem(TOKEN_KEY);
    sessionStorage.removeItem(ACTIVE_KEY);
    setToken(null);
    setJourneyContext(preserveAmbientContext ? normalizedAmbientJourneyContext : null);
    setTextValue("");
    setDoarId(null);
    setRevisionId(null);
    setClassification(null);
    setPreview(null);
    setSpatialSession(null);
    setSpatialSessionLoading(false);
    setJobId(null);
    setStatus("");
    setError("");
    setBusy(false);
    setRecording(false);
    setRefineOpen(false);
    setRefineText("");
    setSourceLabel("");
    setSourceInfo(null);
    setOwnerState("guest");
    setContinuationMode(null);
  };

  const product = useMemo(() => preview?.previewUrl && (preview.webAr || preview.iosAr) ? {
    glbUrl: preview.glbUrl || (preview.webAr ? preview.previewUrl : null),
    usdzUrl: preview.usdzUrl || (preview.iosAr && !preview.webAr ? preview.previewUrl : null),
    posterUrl: sourceInfo?.previewUrl || null,
    placementProfile: sourceInfo?.placementProfile || "floor",
    mode: "static",
    arScale: "auto",
    hideArButton: true,
    autoRotate: false,
  } : null, [preview, sourceInfo]);

  const spatialResult = isSpatialClassification(classification);
  const resultName = sourceInfo?.title
    || sourceLabel
    || (textValue.startsWith("http") ? "Understanding source…" : textValue)
    || "Your DoAR";
  const resultBusyTitle = String(status || "").startsWith("Continuing")
    ? "Opening your DoAR…"
    : String(status || "").startsWith("Updating")
      ? "Updating your DoAR…"
      : String(status || "").startsWith("Reading")
        ? "Understanding the source…"
        : spatialResult
          ? "Preparing AR…"
          : "Working on your DoAR…";

  const ambientContextChanged = Boolean(
    normalizedAmbientJourneyContext
    && journeyContext
    && !sameJourneyContext(normalizedAmbientJourneyContext, journeyContext)
  );

  const phase = phaseFor({
    busy,
    recording,
    status,
    error,
    preview,
    doarId,
    ownerState,
    classification,
  });

  return {
    token,
    accountToken,
    journeyContext,
    ambientJourneyContext: normalizedAmbientJourneyContext,
    ambientContextChanged,
    textValue,
    setTextValue,
    doarId,
    classification,
    preview,
    spatialSession,
    spatialSessionLoading,
    revisionId,
    status,
    setStatus,
    error,
    setError,
    busy,
    recording,
    refineOpen,
    setRefineOpen,
    refineText,
    setRefineText,
    sourceLabel,
    sourceInfo,
    ownerState,
    continuationMode,
    product,
    spatialResult,
    resultName,
    resultBusyTitle,
    phase,
    createFromText,
    resolveRealityContinuation,
    addFile,
    toggleVoice,
    stopVoice,
    continueTo,
    retrySpatial,
    refine,
    reset,
  };
}

export {
  ACCEPTED_FILES,
  ACTIVE_KEY,
  TOKEN_KEY,
  TODOAR_PHASE,
  isSpatialClassification,
  durablePreview,
  durableSourceInfo,
  restoredGatewayState,
};
